import { createContext, useContext } from "react";
import de from "./de.json";
import en from "./en.json";

export type Language = "de" | "en";

const dictionaries: Record<Language, Record<string, string>> = { de, en };

export const DEFAULT_LANGUAGE: Language = "de";

const LANGUAGE_STORAGE_KEY = "bookbase.language";

export function getStoredLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === "en" ? "en" : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function storeLanguage(language: Language) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* ignore (private browsing, quota) */
  }
}

export type TranslateFn = (key: string, fallback: string, params?: Record<string, string | number>) => string;

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => (name in params ? String(params[name]) : match));
}

export function createTranslator(language: Language): TranslateFn {
  return (key, fallback, params) => {
    const dict = dictionaries[language];
    const value = dict[key] ?? (language === DEFAULT_LANGUAGE ? fallback : dictionaries[DEFAULT_LANGUAGE][key] ?? fallback);
    return interpolate(value, params);
  };
}

export interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: TranslateFn;
}

export const I18nContext = createContext<I18nContextValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: createTranslator(DEFAULT_LANGUAGE),
});

export function useTranslation(): I18nContextValue {
  return useContext(I18nContext);
}
